// Cloudflare Turnstile for the guest total (owner, 2026-10-21). The browser
// only ever holds the PUBLIC site key (REACT_APP_TURNSTILE_SITE_KEY); the
// secret lives in the market-checkout function's environment, and the server
// verifies every token with Cloudflare before it spends a HioBuy call. This
// file is convenience, never the control.
//
// Least intrusive: the widget is rendered off-screen and executed on demand
// with appearance 'interaction-only' — nothing is shown unless Cloudflare
// decides a visitor must interact, and then only that small box appears. (The
// widget's MODE — Managed / Non-interactive / Invisible — is chosen on the
// Cloudflare dashboard; this code works with any of them.)
//
// Tokens are single-use and expire after 300 s, so a fresh one is fetched for
// each guest quote. No site key configured → no token (the server, if it has a
// secret, then refuses, which is the safe failure).

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
export const turnstileEnabled = Boolean(SITE_KEY);

let loading = null;
function loadScript() {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;
  loading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = SCRIPT; s.async = true; s.defer = true;
    s.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error('turnstile_missing')));
    s.onerror = () => { loading = null; reject(new Error('turnstile_script_failed')); };
    document.head.appendChild(s);
  });
  return loading;
}

let widgetId = null;
let host = null;
let pending = null;   // { resolve, reject, timer }
function settle(fn, v) {
  if (!pending) return;
  clearTimeout(pending.timer);
  const p = pending; pending = null;
  p[fn](v);
}

/**
 * A fresh token for one guest quote, or null when Turnstile is not configured.
 * Rejects with 'turnstile_failed' / 'turnstile_timeout' — the caller tells the
 * buyer to try again; nothing is charged or sent.
 */
export async function getTurnstileToken({ timeoutMs = 25000 } = {}) {
  if (!SITE_KEY) return null;
  const ts = await loadScript();
  if (!host) {
    host = document.createElement('div');
    host.setAttribute('data-turnstile-host', '1');
    // Out of the layout; if Cloudflare needs an interaction the box becomes
    // visible in place (interaction-only), pinned to the bottom of the screen.
    Object.assign(host.style, { position: 'fixed', insetInline: '0', bottom: '12px', display: 'flex', justifyContent: 'center', zIndex: '1000', pointerEvents: 'auto' });
    document.body.appendChild(host);
  }
  if (widgetId == null) {
    widgetId = ts.render(host, {
      sitekey: SITE_KEY,
      action: 'guest_quote',
      appearance: 'interaction-only',
      execution: 'execute',
      callback: (token) => settle('resolve', token),
      'error-callback': () => settle('reject', new Error('turnstile_failed')),
      'expired-callback': () => { /* a token is used at once; a fresh one is requested per quote */ },
      'timeout-callback': () => settle('reject', new Error('turnstile_timeout')),
    });
  } else {
    ts.reset(widgetId);
  }
  if (pending) settle('reject', new Error('turnstile_superseded'));
  return new Promise((resolve, reject) => {
    pending = { resolve, reject, timer: setTimeout(() => settle('reject', new Error('turnstile_timeout')), timeoutMs) };
    try { ts.execute(widgetId); } catch (e) { settle('reject', e); }
  });
}
